import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import DarkPageHero from "../components/DarkPageHero";

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      question: "What is Financify?",
      answer: "Financify is a digital pre-qualification platform that helps Bahamian residents understand their potential loan eligibility before applying to banks. We evaluate your information against general bank lending criteria and provide estimated loan amounts, interest rates, and monthly payments."
    },
    {
      question: "Is Financify a bank or lender?",
      answer: "No, Financify is NOT a bank or lender. We do not make loan decisions, approve loans, or disburse funds. We are a technology platform that helps you understand which banks you may qualify with based on their publicly available lending criteria."
    },
    {
      question: "Will this affect my credit score?",
      answer: "No. Our pre-qualification check does not affect your CRIF credit report or credit score. We do not perform a hard credit inquiry. This is a soft check based on the information you provide to us."
    },
    {
      question: "How accurate are the estimates?",
      answer: "Our estimates are based on general bank lending criteria and the information you provide. While we strive for accuracy, actual loan approval, amounts, rates, and terms are determined solely by the banks and may differ based on their full underwriting process, including credit checks and document verification."
    },
    {
      question: "What information do I need to provide?",
      answer: "You'll need to provide information about your employment (employer, years employed), income (monthly salary, other income), existing debts (loans, credit cards), and the loan amount you're looking for. Optionally, you can upload identification documents."
    },
    {
      question: "Which banks do you evaluate against?",
      answer: "We currently evaluate against criteria from Fidelity Bank, RBC Royal Bank, Scotiabank, Commonwealth Bank, and CIBC FirstCaribbean. We continuously work to add more banks and update criteria."
    },
    {
      question: "How long does the process take?",
      answer: "The pre-qualification process takes approximately 5 minutes. You'll receive your estimated results immediately after completing the form and payment."
    },
    {
      question: "Is there a fee for using Financify?",
      answer: "Yes, there is a small fee to access your full pre-qualification results. This fee helps us maintain the platform and keep our bank criteria up to date. Promotional codes may be available for discounts."
    },
    {
      question: "Is my information secure?",
      answer: "Yes. We use bank-grade 256-bit SSL encryption to protect your data. We never share your personal information with third parties without your explicit consent. Please review our Privacy Policy for full details."
    },
    {
      question: "What is TDSR?",
      answer: "TDSR stands for Total Debt Service Ratio. It's a percentage that represents how much of your monthly income goes toward debt payments. Banks use TDSR to assess your ability to repay a loan. Generally, banks prefer a TDSR below 40-45%."
    },
    {
      question: "What is a CRIF Report?",
      answer: "A CRIF report in the Bahamas is a comprehensive, electronic document generated by CRIF Information Services Bahamas Limited, the licensed credit bureau for the country. It serves as a detailed record of an individual's or business's credit history, including loan information, payment performance, and outstanding debts (such as credit cards, mortgages, and, over time, utility bills). Banks use CRIF reports to assess creditworthiness when reviewing loan applications."
    },
    {
      question: "Can I save my results?",
      answer: "Yes. If you create an account, your pre-qualification results are saved to your dashboard. You can access them anytime, download PDF reports, and track multiple applications."
    },
    {
      question: "What happens after I get my results?",
      answer: "After receiving your results, you can use them as a guide when approaching banks. Your results show which banks you may qualify with, estimated loan amounts, and terms. You'll still need to formally apply to the bank of your choice."
    },
    {
      question: "Who can I contact for support?",
      answer: "You can reach us at support@financify-caribbean.com for any questions or assistance. We typically respond within 24-48 hours."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DarkPageHero
        overline="FREQUENTLY ASKED QUESTIONS"
        title={<>Everything you need to know <span className="text-[#52BFB8]">— in one place</span>.</>}
        subtitle="Loans, TDSR, banks, KYC, and how Financify keeps your data safe."
      />

      {/* FAQ List */}
      <section className="py-16">
        <div className="container-main">
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold pr-4" style={{ color: '#2D3748' }}>
                    {faq.question}
                  </span>
                  {openIndex === index ? (
                    <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: '#52BFB8' }} />
                  ) : (
                    <ChevronDown className="w-5 h-5 flex-shrink-0 text-gray-400" />
                  )}
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="max-w-3xl mx-auto mt-12 text-center">
            <p className="text-gray-600 mb-4">Still have questions?</p>
            <a 
              href="mailto:support@financify-caribbean.com"
              className="inline-flex items-center gap-2 text-[#52BFB8] font-medium hover:underline"
            >
              Contact us at support@financify-caribbean.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
